import * as React from "react";
import {Suspense, useRef, useState} from "react";
import {BraintreePayPalButtons} from "@paypal/react-paypal-js";
import {useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {CreateBillingAgreementActions} from "@paypal/react-paypal-js/dist/types/types/braintreePayPalButtonTypes";
import {BraintreePayPalCheckoutTokenizationOptions} from "@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout";
import {AddressResponseData, Checkout, CheckoutFactory} from "@connox/typescript-library";
import {getShopId} from "../getShopId";
import {INTERNAL_ERROR_TYPE, PaymentController, Payments} from "./PaymentController";
import {ICheckoutPutParams} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import {getPaymentOptions} from "../../../helpers/paypal/paypalHelperFunctions";
import {ErrorPlaceInfoType, onErrorClosure} from "../../../helpers/paypal/onErrorClosure";
import {
  CreateOrderData,
  OnShippingChangeActions,
  OnShippingChangeData,
} from "@paypal/paypal-js/types/components/buttons";
import {SelectedShippingOption} from "@paypal/paypal-js/types/apis/shipping";
import {isToNorthIreland} from "../../checkout/steps/AddressStep/AddressStep";
import {useCustomerSelector} from "../../../store";
import {buttonStyleObjectExpress, PayPalLoadingSpinner} from "./BraintreeButtonLoader";
import {prepareAddress} from "../../../helpers/paypal/paypalAddressHelpers";
import {ButtonPropType} from "../../../helpers/paypal/paypalTypes";
import {FUNDING_SOURCE} from "@paypal/paypal-js";

/**
 * This component needs the PaypalScriptProvider to be loaded, so a braintree token has to exist
 * @param handleError
 * @param isCartBlocked
 * @param onReturn
 * @constructor
 */
const PayPalExpressButtons: React.FC<ButtonPropType> = ({handleError, isBlocked, onReturn}) => {
  const {cart, countries, setViewState, viewState} = useCartContext();
  const {customer} = useCustomerSelector();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const lastAddressesRef = useRef<AddressResponseData[]>([]);
  const checkoutRef = useRef<Checkout>();

  const onError = onErrorClosure(handleError, "PayPal Express buttons");

  /**
   * BEFORE OPENING POPUP <br/>
   * I'm not using checkoutContext on purpose making sure to only generate a checkout when button is
   * actually clicked.
   */
  const createOrder = async function (data, actions: CreateBillingAgreementActions) {
    try {
      checkoutRef.current = await CheckoutFactory.generateCheckout(getShopId(), cart);
      await checkoutRef.current.put({paymentId: Payments.PaypalExpressBraintree});
      PaymentController.createPayment(checkoutRef.current, Payments.PaypalExpressBraintree);

      const apiShippingOptions = await checkoutRef.current.getAvailableShippingOptions();

      const lastAddressesArray = await customer.getLastAddresses(getShopId());
      const lastDelivery = lastAddressesArray.find((address) => address.isLastDeliveryAddress);

      const paymentOptions = getPaymentOptions({
        apiShippingOptions,
        checkout: checkoutRef.current,
        deliveryAddress: lastDelivery,
      });

      lastAddressesRef.current = lastAddressesArray;

      return await actions.braintree.createPayment(paymentOptions);
    } catch (createOrderError) {
      (createOrderError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnCreateOrder;
      (createOrderError as ErrorPlaceInfoType).paymentSource = (data as CreateOrderData).paymentSource;

      // Regular onError call
      onError(createOrderError as Error | Record<string, unknown>);
      // Manual throw. This is because otherwise this return path
      // couldn't return anything without causing another error.
      // Will be directed to the onError, and there, will be ignored
      throw createOrderError;
    }
  };

  /**
   * AFTER POPUP CLOSING
   * Errors will go to paypal onError method
   * @param data
   * @param actions
   */
  const onApprove = async (
    data: BraintreePayPalCheckoutTokenizationOptions,
    actions: CreateBillingAgreementActions,
  ) => {
    try {
      const tokenized = await actions.braintree.tokenizePayment(data);

      if (!checkoutRef.current) {
        const noCheckoutRefError = new Error("There's no current!");
        (noCheckoutRefError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
        onError(noCheckoutRefError);

        return;
      }

      if (!tokenized.details.shippingAddress) {
        const addressError = new Error("checkout.noValidPayPalAddress");
        (addressError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
        onError(addressError);

        return;
      }

      setViewState({
        ...viewState,
        paypalInfos: {
          nonce: tokenized.nonce,
          paymentSource: (data as BraintreePayPalCheckoutTokenizationOptions & {paymentSource: FUNDING_SOURCE})
            .paymentSource,
        },
      });

      const streetAndNum = tokenized.details.shippingAddress.line1;
      if (streetAndNum.includes("Packstation") || streetAndNum.includes("Postfiliale")) {
        const packingStationError = new Error("delivery to packing station not available for logistics type.");
        (packingStationError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
        onError(packingStationError);

        return;
      }
      const {billingAddress, shippingAddress, email} = tokenized.details;
      const deliveryAddress = prepareAddress(shippingAddress, email);
      // const invoiceAddress = await prepareAddress(billingAddress, email);
      const lastInvoiceAddress = lastAddressesRef.current.find((ls) => ls.isLastInvoiceAddress);
      const lastDeliveryAddress = lastAddressesRef.current.find((ls) => ls.isLastDeliveryAddress);

      await checkoutRef.current
        .put({
          address: {
            delivery: {
              ...deliveryAddress,
              email: lastInvoiceAddress?.email || checkoutRef.current?.invoiceAddress?.email || deliveryAddress.email,
              phone: lastDeliveryAddress?.phone || checkoutRef.current?.deliveryAddress?.phone || deliveryAddress.phone,
            },
            invoice: {
              ...deliveryAddress,
              email: lastInvoiceAddress?.email || checkoutRef.current?.invoiceAddress?.email || deliveryAddress.email,
            },
          },
        } as ICheckoutPutParams)
        .then(onReturn)
        .catch((putError: Error) => {
          (putError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
          onError(putError);
        });
    } catch (err) {
      (err as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
      (err as ErrorPlaceInfoType).paymentSource = (
        data as BraintreePayPalCheckoutTokenizationOptions & {paymentSource: FUNDING_SOURCE}
      ).paymentSource;
      onError(err as Error | Record<string, unknown>);
    }
  };

  const onShippingChange = async (data: OnShippingChangeData, actions: OnShippingChangeActions) => {
    // region Check for correct delivery address
    const countryCode = data.shipping_address?.country_code;
    const postalCode = data.shipping_address?.postal_code;

    let isError = false;
    if (!countryCode || !postalCode) {
      // Eventually no address at all
      isError = true;
    } else if (!countries.some((country) => country.countryCode === countryCode)) {
      // Country code not allowed
      isError = true;
    } else if (isToNorthIreland(countryCode, postalCode)) {
      // North Ireland also not allowed
      isError = true;
    }
    if (isError) {
      await actions.reject();

      return;
    }
    // endregion

    // region Check for new shipping type
    const selectedShippingId = Number(
      (data.selected_shipping_option as (SelectedShippingOption & {id: string}) | undefined)?.id || "0",
    );

    if (!checkoutRef.current) {
      await actions.reject();

      return;
    }

    // Only set if the new one is actually a different to the already set one
    if (checkoutRef.current.logisticsTypeId !== selectedShippingId && selectedShippingId > 0) {
      await checkoutRef.current
        .put({
          logisticsTypeId: selectedShippingId,
        })
        .then(actions.resolve)
        .catch(actions.reject);
    }
    // endregion
  };

  /**
   * Reset paymentId, when customer cancels PP payment
   */
  const onCancel = () =>
    void CheckoutFactory.generateCheckout(getShopId(), cart).then((checkout) => {
      void checkout.put({paymentId: null});
    });

  return (
    <Suspense fallback={null}>
      {!isInitialized && <PayPalLoadingSpinner />}
      <BraintreePayPalButtons
        disabled={isBlocked}
        style={buttonStyleObjectExpress}
        onError={onError}
        createOrder={createOrder}
        onApprove={onApprove}
        onShippingChange={onShippingChange}
        onCancel={onCancel}
        onInit={() => setIsInitialized(true)}
      />
    </Suspense>
  );
};

export default PayPalExpressButtons;
